import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import toast from "react-hot-toast";
import { FiCalendar, FiLink, FiSearch } from "react-icons/fi";
import { createCampaignApi, updateCampaignApi } from "./api";
import { listShortUrlsApiPO } from "../list/api";
import UserLoginData from "../Auth/UserLoginData";

const EMPTY_CODES = [];
const PAGE_SIZE = 50;

export const CreateCampaignModal = ({
  show,
  onHide,
  onSuccess,
  campaignToEdit = null,
  initialSelectedCodes = EMPTY_CODES,
  availableClients = [],
  currentRole = "admin",
  currentClientId = "all",
}) => {
  const { isLoggedIn } = UserLoginData();

  const userId = isLoggedIn?.response?._id;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedClient, setSelectedClient] = useState(
    currentRole === "client" ? userId : currentClientId !== "all" ? currentClientId : ""
  );
  const [utmCampaign, setUtmCampaign] = useState("");
  const [selectedCodes, setSelectedCodes] = useState(new Set());

  // Link picker filters
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [linksError, setLinksError] = useState("");
  const [linkPage, setLinkPage] = useState(0);
  const [linkCount, setLinkCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [recencyFilter, setRecencyFilter] = useState("all"); // 'all' | '7d' | '30d' | '90d'
  const [submitting, setSubmitting] = useState(false);

  // Initialize form state when opened or campaignToEdit changes
  useEffect(() => {
    if (!show) return;
    setSearchQuery("");
    setRecencyFilter("all");
    setLinkPage(0);

    if (campaignToEdit) {
      setName(campaignToEdit.name || "");
      setDescription(campaignToEdit.description || "");
      setSelectedClient(campaignToEdit.clientId || "");
      setUtmCampaign(campaignToEdit.utm_campaign || "");
      setSelectedCodes(new Set(campaignToEdit.short_codes || []));
    } else {
      setName("");
      setDescription("");
      setSelectedClient(
        currentRole === "client" ? userId : currentClientId !== "all" ? currentClientId : ""
      );
      setUtmCampaign("");
      setSelectedCodes(new Set(initialSelectedCodes || []));
    }
  }, [show, campaignToEdit, currentClientId, currentRole, initialSelectedCodes, userId]);

  useEffect(() => {
    if (!show) return;
    let isCurrentRequest = true;
    const targetClient =
      currentRole === "client"
        ? userId
        : selectedClient;

    if (!targetClient || targetClient === "all") {
      setLinks([]);
      setLinkCount(0);
      setLoadingLinks(false);
      return;
    }
    setLoadingLinks(true);
    setLinksError("");
    if (linkPage === 0) setLinks([]);
    const timer = setTimeout(async () => {
      const [data, error] = await listShortUrlsApiPO(
        currentRole === "operational" ? [targetClient] : targetClient,
        currentRole,
        linkPage,
        PAGE_SIZE,
        { search: searchQuery, recency: recencyFilter, sortNewest: true }
      );
      if (!isCurrentRequest) return;
      if (error) {
        const message = error.response?.data?.message || "Could not load short links.";
        setLinksError(message);
        toast.error(message);
      } else {
        setLinks((previous) => linkPage === 0 ? data.urls : [...previous, ...data.urls]);
        setLinkCount(data.count);
      }
      setLoadingLinks(false);
    }, 250);
    return () => {
      isCurrentRequest = false;
      clearTimeout(timer);
    };
  }, [show, currentRole, selectedClient, userId, linkPage, searchQuery, recencyFilter]);

  const filteredLinks = links;

  const toggleSelectLink = (code) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const handleSelectAllFiltered = () => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      filteredLinks.forEach((l) => next.add(l.short_code));
      return next;
    });
  };

  const handleDeselectAllFiltered = () => setSelectedCodes(new Set());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }

    const clientId = currentRole === "client" ? isLoggedIn?.response?._id : selectedClient;
    if (!clientId || clientId === "all") {
      toast.error("Please select a client for this campaign");
      return;
    }

    const matchedClient = availableClients.find((c) => c._id === clientId);
    const clientName = matchedClient?.username || isLoggedIn?.response?.username || "";

    setSubmitting(true);
    const payload = {
      name: name.trim(),
      description: description.trim(),
      clientId,
      clientName,
      utm_campaign: utmCampaign.trim(),
      short_codes: Array.from(selectedCodes),
      loginUserId: isLoggedIn?.response?._id,
    };

    let data, error;
    if (campaignToEdit) {
      [data, error] = await updateCampaignApi(campaignToEdit._id, payload);
    } else {
      [data, error] = await createCampaignApi(payload);
    }

    setSubmitting(false);

    if (error) {
      toast.error(error.response?.data?.message || "Failed to save campaign");
    } else {
      toast.success(campaignToEdit ? "Campaign updated!" : "Campaign created!");
      if (onSuccess) onSuccess(data?.campaign);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "18px", fontWeight: 700 }}>
          {campaignToEdit ? "Edit Campaign" : "Create New Campaign"}
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body className="campaign-modal-body">
          <div className="row g-3">
            <div className="col-md-7">
              <label className="form-label small fw-bold" htmlFor="campaign-name">
                Campaign Name *
              </label>
              <input
                id="campaign-name"
                type="text"
                className="form-control"
                placeholder="e.g. Summer Promo 2026, Product Launch..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {currentRole !== "client" && (
              <div className="col-md-5">
                <label className="form-label small fw-bold" htmlFor="campaign-client">
                  Client *
                </label>
                <select
                  id="campaign-client"
                  className="form-select"
                  value={selectedClient}
                  onChange={(e) => {
                    setSelectedClient(e.target.value);
                    setSelectedCodes(new Set());
                    setLinkPage(0);
                  }}
                  disabled={Boolean(campaignToEdit)}
                  required
                >
                  <option value="">Select client...</option>
                  {availableClients.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.username}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="col-12">
              <label className="form-label small fw-bold" htmlFor="campaign-desc">
                Description (Optional)
              </label>
              <textarea
                id="campaign-desc"
                className="form-control"
                rows={2}
                placeholder="Brief notes about the goals or audience of this campaign..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label small fw-bold" htmlFor="campaign-utm">
                UTM Campaign Tag (Optional)
              </label>
              <input
                id="campaign-utm"
                type="text"
                className="form-control"
                placeholder="e.g. summer_promo_q3"
                value={utmCampaign}
                onChange={(e) => setUtmCampaign(e.target.value)}
              />
            </div>
          </div>

          {/* Link Picker Section */}
          <div className="campaign-link-picker mt-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <FiLink className="text-muted" aria-hidden="true" />
                <strong style={{ fontSize: "14px" }}>Club Links into Campaign</strong>
                <span className="badge bg-primary rounded-pill">
                  {selectedCodes.size} selected
                </span>
              </div>

              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleSelectAllFiltered}
                  disabled={filteredLinks.length === 0}
                >
                  Select all visible
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleDeselectAllFiltered}
                  disabled={selectedCodes.size === 0}
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Filter controls */}
            <div className="campaign-link-picker-filters">
              <div className="input-group input-group-sm" style={{ maxWidth: "260px" }}>
                <span className="input-group-text bg-transparent">
                  <FiSearch aria-hidden="true" />
                </span>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search title, code, url..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setLinkPage(0);
                  }}
                  aria-label="Search campaign links"
                />
              </div>

              <div className="input-group input-group-sm" style={{ maxWidth: "200px" }}>
                <span className="input-group-text bg-transparent">
                  <FiCalendar aria-hidden="true" />
                </span>
                <select
                  className="form-select"
                  value={recencyFilter}
                  onChange={(e) => {
                    setRecencyFilter(e.target.value);
                    setLinkPage(0);
                  }}
                  aria-label="Filter links by recency"
                >
                  <option value="all">Created: All time</option>
                  <option value="7d">Created: Last 7 days</option>
                  <option value="30d">Created: Last 30 days</option>
                  <option value="90d">Created: Last 90 days</option>
                </select>
              </div>
            </div>

            {/* Link Rows */}
            <div className="campaign-link-picker-table">
              {loadingLinks && linkPage === 0 ? (
                <div className="text-center py-4 text-muted small">Loading short links...</div>
              ) : linksError ? (
                <div role="alert" className="text-center py-4 text-danger small">{linksError}</div>
              ) : filteredLinks.length === 0 ? (
                <div className="text-center py-4 text-muted small">
                  {selectedClient || currentRole === "client"
                    ? "No matching links found for this client."
                    : "Select a client to browse their short links."}
                </div>
              ) : (
                filteredLinks.map((link) => {
                  const isChecked = selectedCodes.has(link.short_code);
                  return (
                    <div
                      key={link.short_code}
                      className="campaign-link-row"
                      onClick={() => toggleSelectLink(link.short_code)}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input mt-0"
                        checked={isChecked}
                        onChange={() => toggleSelectLink(link.short_code)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${link.title || link.short_code}`}
                      />
                      <div className="campaign-link-info">
                        <span className="campaign-link-title">
                          {link.title || "Untitled link"}
                        </span>
                        <span className="campaign-link-sub">
                          <code>/{link.short_code}</code>
                          <span>·</span>
                          <span className="text-truncate" style={{ maxWidth: "220px" }}>
                            {link.url}
                          </span>
                          <span>·</span>
                          <span>{Number(link.clicks_count || 0).toLocaleString()} clicks</span>
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {links.length < linkCount && !linksError && (
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="text-muted small">{links.length} of {linkCount} matching links</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={loadingLinks}
                  onClick={() => setLinkPage((page) => page + 1)}
                >
                  {loadingLinks ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={onHide}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Saving..." : campaignToEdit ? "Save Changes" : "Create Campaign"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default CreateCampaignModal;
